/**
 * O esquema do IndexedDB — `PWA §7.2`.
 *
 * Banco `fs-app`, versao gerenciada por migracao incremental no `upgrade`.
 *
 * ## A separacao que resolve C-02 estruturalmente
 *
 * `PWA §4.1`: **duas camadas com responsabilidades separadas**. Cache Storage (camada 1) nao
 * tem nocao de sessao — nao e limpo no logout, e trivialmente inspecionavel no DevTools e
 * sobrevive a troca de usuario no mesmo aparelho, que e cenario **comum** aqui (celular
 * compartilhado em familia). Por isso o service worker nao guarda nada autenticado, e todo dado
 * de negocio offline vive aqui, na camada 2, com ciclo de vida ligado a sessao.
 *
 * A consequencia pratica e que o service worker **nao precisa entender o que e dado de saude**.
 * Ele tem uma regra simples e auditavel; a inteligencia sobre sensibilidade vive aqui, junto do
 * modelo de dominio.
 *
 * ## Biblioteca: `idb` (~1,2 KB gz)
 *
 * `PWA §7.1` compara com Dexie (~25 KB, "20x em bytes por recursos que 7 object stores simples
 * nao pedem") e `localforage` (~9 KB, "abstrai IndexedDB como chave-valor, perdendo indices e
 * transacoes"). Num projeto onde bytes sao a franquia do usuario, 1,2 KB e a resposta.
 */

/** `PWA §7.2` — os object stores, com o que cada um pode conter. */
export const NOME_DO_BANCO = 'fs-app';
export const VERSAO_DO_BANCO = 1;

export type NomeDeStore =
  | 'kv'
  | 'catalog'
  | 'queries'
  | 'cart'
  | 'orders_mirror'
  | 'outbox'
  | 'health_vault'
  | 'keys';

export interface DefinicaoDeStore {
  readonly nome: NomeDeStore;
  readonly keyPath: string;
  readonly indices: readonly { readonly nome: string; readonly keyPath: string }[];
  /**
   * `PWA §7.5` — limpo no logout?
   *
   * `kv` e o unico `false`, e o motivo esta no documento: tema, `saveData` e ultimo `storeId`
   * sao **preferencia do aparelho**, nao do titular. Apagar o tema escuro de alguem porque ela
   * saiu da conta e um bug com cara de zelo.
   */
  readonly limpaNoLogout: boolean;
  /** Dias ate expirar. `null` = sem TTL. */
  readonly ttlDias: number | null;
  readonly cifrado: boolean;
}

export const STORES: readonly DefinicaoDeStore[] = [
  {
    nome: 'kv',
    keyPath: 'chave',
    indices: [],
    limpaNoLogout: false,
    ttlDias: null,
    cifrado: false,
  },
  {
    // Ficha de produto vista nos ultimos 7 dias, **sem preco e sem estoque** (`PWA §6.2`).
    // Dado estavel: nome, imagem, apresentacao, bula, exige receita.
    nome: 'catalog',
    keyPath: 'sku',
    indices: [
      { nome: 'byCategory', keyPath: 'categoryId' },
      { nome: 'bySeenAt', keyPath: 'seenAt' },
    ],
    limpaNoLogout: false,
    ttlDias: 7,
    cifrado: false,
  },
  {
    // Cache do TanStack Query, **apenas** para chaves na allowlist do `PWA §7.3`, que e o
    // `PERSISTED_QUERY_KEYS` do `@farmacia/api-client/persist`.
    nome: 'queries',
    keyPath: 'queryHash',
    indices: [],
    limpaNoLogout: true,
    ttlDias: 1,
    cifrado: false,
  },
  {
    // `lineId, sku, qty, addedAt, note` — **sem preco**, nos dois lados (`PWA §8.1`).
    nome: 'cart',
    keyPath: 'lineId',
    indices: [{ nome: 'byAddedAt', keyPath: 'addedAt' }],
    limpaNoLogout: true,
    ttlDias: null,
    cifrado: false,
  },
  {
    // O caso de uso numero um do offline: **o codigo de retirada**. O cliente chega na loja e o
    // sinal cai. Nome dos itens so entra com consentimento explicito, cifrado (`itemsEnc`).
    nome: 'orders_mirror',
    keyPath: 'orderId',
    indices: [{ nome: 'byCreatedAt', keyPath: 'createdAt' }],
    limpaNoLogout: true,
    ttlDias: 90,
    cifrado: false,
  },
  {
    nome: 'outbox',
    keyPath: 'id',
    indices: [
      { nome: 'byStatus', keyPath: 'status' },
      { nome: 'byNextAttemptAt', keyPath: 'nextAttemptAt' },
    ],
    limpaNoLogout: true,
    ttlDias: 1,
    cifrado: false,
  },
  {
    nome: 'health_vault',
    keyPath: 'id',
    indices: [],
    limpaNoLogout: true,
    ttlDias: 90,
    cifrado: true,
  },
  {
    // `CryptoKey` **nao extraivel** (AES-GCM 256). Um XSS consegue *usar* a chave, mas nao
    // exporta-la para um servidor remoto — mitigacao parcial e honesta, nao completa
    // (`PWA §7.4`).
    nome: 'keys',
    keyPath: 'name',
    indices: [],
    limpaNoLogout: true,
    ttlDias: null,
    cifrado: false,
  },
];

/**
 * A ordem de limpeza do `PWA §7.5`: **as chaves primeiro**.
 *
 * "Destruicao: `keys.clear()` no logout **antes** de apagar os dados (se a limpeza for
 * interrompida, sobra apenas texto cifrado sem chave)." Uma limpeza interrompida no meio e o
 * caso normal num Android modesto que mata a aba; a ordem e o que faz esse caso terminar seguro.
 */
export const ORDEM_DE_LIMPEZA: readonly NomeDeStore[] = [
  'keys',
  'health_vault',
  'orders_mirror',
  'queries',
  'cart',
  'outbox',
];

/** `PWA §7.5`, passo 7: preservar tema e `saveData`; remover o resto do `localStorage`. */
export const CHAVES_DE_LOCALSTORAGE_PRESERVADAS: readonly string[] = ['tema', 'saveData'];

/** `PWA §7.1` — `localStorage` guarda **apenas** flags sincronas de boot, ~1 KB no total. */
export const CHAVES_DE_LOCALSTORAGE: readonly string[] = [
  'tema',
  'saveData',
  'storeId',
  'zoneId',
  'onboarding',
  'installPromptState',
];

/** `PWA §7.3` — "sanitizado + limite de 20". */
export const LIMITE_DE_PEDIDOS_ESPELHADOS = 20;
